import React, { useCallback, useEffect, useState } from 'react'
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  MegaphoneIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline'
import { Heading, Text, Button, Input, LoadingSpinner } from '../ui'
import { BRAND_COLOR } from '../../constants'
import { homePopupService } from '../../services/homePopup'
import { useUserLogs } from '../../hooks/useUserLogs'
import type { CreateHomePopupPayload, HomePopup } from '../../types/homePopup'

interface HomePopupsTabProps {
  canInsert: boolean
  canUpdate: boolean
  canDelete: boolean
  onRequestCreate?: boolean
  onCreateHandled?: () => void
}

const emptyForm: CreateHomePopupPayload = {
  title: '',
  body: '',
  starts_at: '',
  ends_at: '',
}

/** Converte ISO/timestamptz para valor de input datetime-local (hora local). */
function toDatetimeLocalValue(iso: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** Converte valor datetime-local para ISO enviado ao Supabase. */
function fromDatetimeLocalValue(value: string): string {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toISOString()
}

function formatDateTime(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function getStatus(item: HomePopup): { label: string; className: string } {
  const now = Date.now()
  const start = new Date(item.starts_at).getTime()
  const end = new Date(item.ends_at).getTime()
  if (now < start) {
    return {
      label: 'Agendado',
      className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
    }
  }
  if (now > end) {
    return {
      label: 'Encerrado',
      className: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
    }
  }
  return {
    label: 'Ativo',
    className: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300',
  }
}

const HomePopupsTab: React.FC<HomePopupsTabProps> = ({
  canInsert,
  canUpdate,
  canDelete,
  onRequestCreate,
  onCreateHandled,
}) => {
  const { logCreate, logUpdate, logDelete } = useUserLogs()
  const [items, setItems] = useState<HomePopup[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [editingItem, setEditingItem] = useState<HomePopup | null>(null)
  const [formData, setFormData] = useState<CreateHomePopupPayload>(emptyForm)
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})
  const [modalLoading, setModalLoading] = useState(false)

  const fetchData = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await homePopupService.list()
      setItems(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar avisos')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const openModal = useCallback(
    (item?: HomePopup) => {
      if (item) {
        if (!canUpdate) {
          alert('Você não tem permissão para editar avisos.')
          return
        }
        setEditingItem(item)
        setFormData({
          title: item.title,
          body: item.body,
          starts_at: toDatetimeLocalValue(item.starts_at),
          ends_at: toDatetimeLocalValue(item.ends_at),
        })
      } else {
        if (!canInsert) {
          alert('Você não tem permissão para criar avisos.')
          return
        }
        setEditingItem(null)
        setFormData(emptyForm)
      }
      setValidationErrors({})
      setShowModal(true)
    },
    [canInsert, canUpdate]
  )

  useEffect(() => {
    if (onRequestCreate) {
      openModal()
      onCreateHandled?.()
    }
  }, [onRequestCreate, openModal, onCreateHandled])

  const closeModal = () => {
    setShowModal(false)
    setEditingItem(null)
    setFormData(emptyForm)
    setValidationErrors({})
  }

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {}
    if (!formData.title.trim()) errors.title = 'Título é obrigatório'
    if (!formData.body.trim()) errors.body = 'Texto é obrigatório'
    if (!formData.starts_at) errors.starts_at = 'Data/hora de início é obrigatória'
    if (!formData.ends_at) errors.ends_at = 'Data/hora de fim é obrigatória'
    if (formData.starts_at && formData.ends_at) {
      const start = new Date(formData.starts_at).getTime()
      const end = new Date(formData.ends_at).getTime()
      if (!Number.isNaN(start) && !Number.isNaN(end) && end <= start) {
        errors.ends_at = 'A data/hora de fim deve ser posterior ao início'
      }
    }
    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) return
    try {
      setModalLoading(true)
      const payload: CreateHomePopupPayload = {
        title: formData.title.trim(),
        body: formData.body.trim(),
        starts_at: fromDatetimeLocalValue(formData.starts_at),
        ends_at: fromDatetimeLocalValue(formData.ends_at),
      }

      if (editingItem) {
        const updated = await homePopupService.update(editingItem.id, payload)
        await logUpdate(
          'other',
          updated.id,
          `Atualizou aviso da home: ${updated.title}`,
          {
            title: editingItem.title,
            starts_at: editingItem.starts_at,
            ends_at: editingItem.ends_at,
          },
          {
            title: updated.title,
            starts_at: updated.starts_at,
            ends_at: updated.ends_at,
          }
        )
      } else {
        const created = await homePopupService.create(payload)
        await logCreate('other', created.id, `Criou aviso da home: ${created.title}`)
      }

      closeModal()
      await fetchData()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erro ao salvar aviso')
    } finally {
      setModalLoading(false)
    }
  }

  const handleDelete = async (item: HomePopup) => {
    if (!canDelete) {
      alert('Você não tem permissão para excluir avisos.')
      return
    }
    if (!window.confirm(`Excluir o aviso "${item.title}"?`)) return
    try {
      await homePopupService.softDelete(item.id)
      await logDelete('other', item.id, `Excluiu aviso da home: ${item.title}`)
      await fetchData()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Erro ao excluir aviso')
    }
  }

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center p-12">
        <LoadingSpinner />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center p-12">
        <div className="text-center max-w-md">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <Text size="lg" className="text-red-600 dark:text-red-400 mb-2">
            Erro ao carregar avisos
          </Text>
          <Text size="sm" className="text-gray-600 dark:text-gray-400 mb-4">
            {error}
          </Text>
          <Button
            onClick={fetchData}
            className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 mx-auto"
          >
            <ArrowPathIcon className="h-4 w-4" />
            Tentar novamente
          </Button>
        </div>
      </div>
    )
  }

  return (
    <>
      <div className="flex-1 overflow-y-auto p-6">
        <div className="mb-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40 px-4 py-3">
          <Text size="sm" className="text-gray-600 dark:text-gray-400">
            Avisos exibidos em popup ao entrar na home. Defina o período de exibição; o usuário pode
            marcar para não ver novamente no mesmo navegador.
          </Text>
        </div>

        {items.length === 0 ? (
          <div className="text-center py-12">
            <MegaphoneIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <Text size="lg" className="text-gray-500 mb-2">
              Nenhum aviso cadastrado
            </Text>
            <Text size="sm" className="text-gray-400">
              Clique em &quot;Novo aviso&quot; para criar o primeiro
            </Text>
            {canInsert && (
              <Button
                onClick={() => openModal()}
                className="mt-4 flex items-center gap-2 mx-auto"
                style={{ backgroundColor: BRAND_COLOR }}
              >
                <PlusIcon className="h-5 w-5" />
                Novo aviso
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Título
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Início
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Fim
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {items.map((item) => {
                  const status = getStatus(item)
                  return (
                    <tr key={item.id}>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white max-w-xs">
                        <div className="truncate" title={item.title}>
                          {item.title}
                        </div>
                        <Text size="xs" className="text-gray-400 truncate mt-0.5" title={item.body}>
                          {item.body}
                        </Text>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {formatDateTime(item.starts_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {formatDateTime(item.ends_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${status.className}`}
                        >
                          {status.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-2">
                          {canUpdate && (
                            <div title="Editar">
                              <Button
                                onClick={() => openModal(item)}
                                size="sm"
                                variant="outline"
                                className="flex items-center justify-center"
                              >
                                <PencilIcon className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                          {canDelete && (
                            <div title="Excluir">
                              <Button
                                onClick={() => handleDelete(item)}
                                size="sm"
                                variant="outline"
                                className="flex items-center justify-center text-red-600 hover:text-red-700"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <Heading as="h2" size="2xl" className="text-gray-900 dark:text-white">
                {editingItem ? 'Editar aviso' : 'Novo aviso'}
              </Heading>
              <Button
                onClick={closeModal}
                variant="outline"
                size="sm"
                className="flex items-center gap-1"
              >
                <XMarkIcon className="h-4 w-4" />
                Fechar
              </Button>
            </div>

            <div className="p-6 overflow-y-auto flex-1 space-y-4">
              <div>
                <Text size="sm" className="mb-2">
                  Título <span className="text-red-500">*</span>
                </Text>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
                  placeholder="Título do aviso..."
                  className={validationErrors.title ? 'border-red-500' : ''}
                />
                {validationErrors.title && (
                  <Text size="sm" className="text-red-500 mt-1">
                    {validationErrors.title}
                  </Text>
                )}
              </div>

              <div>
                <Text size="sm" className="mb-2">
                  Texto <span className="text-red-500">*</span>
                </Text>
                <textarea
                  value={formData.body}
                  onChange={(e) => setFormData((prev) => ({ ...prev, body: e.target.value }))}
                  placeholder="Mensagem que será exibida no popup..."
                  rows={6}
                  className={`w-full rounded-lg border px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                    validationErrors.body ? 'border-red-500' : ''
                  }`}
                />
                {validationErrors.body && (
                  <Text size="sm" className="text-red-500 mt-1">
                    {validationErrors.body}
                  </Text>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Text size="sm" className="mb-2">
                    Início da exibição <span className="text-red-500">*</span>
                  </Text>
                  <Input
                    type="datetime-local"
                    value={formData.starts_at}
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, starts_at: e.target.value }))
                    }
                    className={validationErrors.starts_at ? 'border-red-500' : ''}
                  />
                  {validationErrors.starts_at && (
                    <Text size="sm" className="text-red-500 mt-1">
                      {validationErrors.starts_at}
                    </Text>
                  )}
                </div>
                <div>
                  <Text size="sm" className="mb-2">
                    Fim da exibição <span className="text-red-500">*</span>
                  </Text>
                  <Input
                    type="datetime-local"
                    value={formData.ends_at}
                    onChange={(e) => setFormData((prev) => ({ ...prev, ends_at: e.target.value }))}
                    className={validationErrors.ends_at ? 'border-red-500' : ''}
                  />
                  {validationErrors.ends_at && (
                    <Text size="sm" className="text-red-500 mt-1">
                      {validationErrors.ends_at}
                    </Text>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <Button onClick={closeModal} variant="outline" disabled={modalLoading}>
                Cancelar
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={modalLoading}
                style={{ backgroundColor: BRAND_COLOR }}
                className="text-white"
              >
                {modalLoading ? 'Salvando...' : editingItem ? 'Salvar' : 'Criar'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default HomePopupsTab
